import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Truck, LifeBuoy, Share2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminStoreSettings() {
  const [form, setForm] = useState({
    free_shipping_threshold: 1999, support_email: "", support_phone: "",
    social: { instagram: "", facebook: "", twitter: "", whatsapp: "" },
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setForm({
        free_shipping_threshold: data.free_shipping_threshold ?? 1999,
        support_email: data.support_email || "",
        support_phone: data.support_phone || "",
        social: { instagram: "", facebook: "", twitter: "", whatsapp: "", ...(data.social || {}) },
      });
    });
  }, []);

  const setSocial = (k, v) => setForm((f) => ({ ...f, social: { ...f.social, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        free_shipping_threshold: Number(form.free_shipping_threshold) || 0,
        support_email: form.support_email,
        support_phone: form.support_phone,
        social: form.social,
      });
      toast.success("Store settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="admin-store-settings">
      <div className="rounded-2xl border bg-white dark:bg-slate-900 p-5">
        <h3 className="font-display font-bold flex items-center gap-2 mb-3"><Truck size={18} className="text-[#E63946]" /> Shipping</h3>
        <Label className="text-xs">Free shipping over (₹)</Label>
        <Input type="number" value={form.free_shipping_threshold} onChange={(e) => setForm({ ...form, free_shipping_threshold: e.target.value })} data-testid="settings-free-shipping" />
        <p className="text-xs text-slate-400 mt-1">Shown in the announcement bar and used as the free-shipping cut-off.</p>
      </div>

      <div className="rounded-2xl border bg-white dark:bg-slate-900 p-5">
        <h3 className="font-display font-bold flex items-center gap-2 mb-3"><LifeBuoy size={18} className="text-[#E63946]" /> Support contact</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Support email</Label><Input value={form.support_email} onChange={(e) => setForm({ ...form, support_email: e.target.value })} data-testid="settings-email" /></div>
          <div><Label className="text-xs">Support phone</Label><Input value={form.support_phone} onChange={(e) => setForm({ ...form, support_phone: e.target.value })} data-testid="settings-phone" /></div>
        </div>
      </div>

      <div className="rounded-2xl border bg-white dark:bg-slate-900 p-5">
        <h3 className="font-display font-bold flex items-center gap-2 mb-3"><Share2 size={18} className="text-[#E63946]" /> Social links</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {["instagram", "facebook", "twitter", "whatsapp"].map((k) => (
            <div key={k}><Label className="text-xs capitalize">{k}</Label><Input value={form.social[k] || ""} onChange={(e) => setSocial(k, e.target.value)} placeholder="https://..." data-testid={`settings-social-${k}`} /></div>
          ))}
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="bg-[#E63946] hover:bg-[#d12836]" data-testid="settings-save">
        {saving ? "Saving..." : "Save store settings"}
      </Button>
    </div>
  );
}
